import { useEffect, useLayoutEffect, useRef } from "react";
import {
  getLatestTask,
  getVerbBuildStatusFromTasks,
  TaskName,
} from "components/Environment/utils/environtmentUtils";
import type { Environment } from "components/Environment/utils/types";
import { VerbBuildStatus } from "models/Workspace.model";

import {
  fetchLatestPersistedEnvironmentBuildLogs,
  isEnvironmentLogCancellation,
  pollEnvironmentLogs,
  type EnvironmentLogSource,
} from "./environmentLogs";

export type EnvironmentLogMode = "inactive" | "stream" | "persisted";

export interface EnvironmentLogTarget {
  buildStatus: VerbBuildStatus;
  executionKey: string;
  environmentId: string;
  mode: EnvironmentLogMode;
}

export function getEnvironmentLogTarget(
  environment: Environment | null | undefined,
  isActive: boolean,
  allowStreaming = true,
): EnvironmentLogTarget {
  const buildStatus = getVerbBuildStatusFromTasks(environment?.tasks || []);
  const environmentId =
    environment?.environment_id || environment?.workspaceId || "";
  const buildTaskId =
    getLatestTask(TaskName.ENVIRONMENT_BUILD, environment?.tasks || [])
      ?.task_id || "";
  const canLoadLogs =
    buildStatus === VerbBuildStatus.Pending ||
    buildStatus === VerbBuildStatus.Building ||
    buildStatus === VerbBuildStatus.Completed ||
    buildStatus === VerbBuildStatus.CreateFailed;
  const isStreaming =
    buildStatus === VerbBuildStatus.Pending ||
    buildStatus === VerbBuildStatus.Building;

  return {
    buildStatus,
    executionKey: `${environmentId}:${buildTaskId}`,
    environmentId,
    mode:
      !isActive || !environmentId || !canLoadLogs
        ? "inactive"
        : isStreaming && allowStreaming
          ? "stream"
          : isStreaming
            ? "inactive"
            : "persisted",
  };
}

interface UseEnvironmentLogsOptions {
  environmentId: string;
  executionKey: string;
  mode: EnvironmentLogMode;
  onLogs: (content: string, source: EnvironmentLogSource) => void;
  onError?: (error: unknown) => void;
}

interface PersistedHandoffCache {
  executionKey: string;
  environmentLogId: string;
  environmentId: string;
}

export function useEnvironmentLogs(options: UseEnvironmentLogsOptions): void {
  const onLogsRef = useRef(options.onLogs);
  const onErrorRef = useRef(options.onError);
  const activeRunsRef = useRef(new Map<string, Promise<void>>());
  const persistedHandoffRef = useRef<PersistedHandoffCache | null>(null);

  useLayoutEffect(() => {
    onLogsRef.current = options.onLogs;
    onErrorRef.current = options.onError;
  }, [options.onError, options.onLogs]);

  useEffect(() => {
    if (!options.environmentId || options.mode === "inactive") return;

    const controller = new AbortController();
    const run = async (): Promise<void> => {
      if (controller.signal.aborted) return;

      if (options.mode === "stream") {
        await pollEnvironmentLogs({
          environmentId: options.environmentId,
          signal: controller.signal,
          onLogs: (content, source) => {
            if (!controller.signal.aborted) {
              if (
                source === "live" &&
                persistedHandoffRef.current?.environmentId ===
                  options.environmentId &&
                persistedHandoffRef.current.executionKey ===
                  options.executionKey
              ) {
                persistedHandoffRef.current = null;
              }
              onLogsRef.current(content, source);
            }
          },
          onPersisted: (environmentLogId) => {
            if (!controller.signal.aborted) {
              persistedHandoffRef.current = {
                executionKey: options.executionKey,
                environmentLogId,
                environmentId: options.environmentId,
              };
            }
          },
        });
        return;
      }

      const persistedHandoff = persistedHandoffRef.current;
      const currentEnvironmentLogId =
        persistedHandoff?.environmentId === options.environmentId &&
        persistedHandoff.executionKey === options.executionKey
          ? persistedHandoff.environmentLogId
          : undefined;

      const persisted = await fetchLatestPersistedEnvironmentBuildLogs({
        currentEnvironmentLogId,
        environmentId: options.environmentId,
        signal: controller.signal,
      });
      if (persisted && !controller.signal.aborted) {
        onLogsRef.current(
          new TextDecoder().decode(persisted.content),
          "persisted",
        );
      }
    };

    const previousRun =
      activeRunsRef.current.get(options.environmentId) ?? Promise.resolve();
    const currentRun = previousRun
      .catch(() => undefined)
      .then(run)
      .catch((error) => {
        if (!isEnvironmentLogCancellation(error, controller.signal)) {
          onErrorRef.current?.(error);
        }
      });
    activeRunsRef.current.set(options.environmentId, currentRun);
    void currentRun.finally(() => {
      if (activeRunsRef.current.get(options.environmentId) === currentRun) {
        activeRunsRef.current.delete(options.environmentId);
      }
    });

    return () => controller.abort();
  }, [options.environmentId, options.executionKey, options.mode]);
}
